// Paso 1 — TU SALA
//
// Inputs: largo, ancho, altura. Selector de materiales por superficie.
// Validación: largo y ancho > 1, altura > 1,5, < 20.

import { state, update } from '/src/shared/state.js';
import { t } from '/src/shared/i18n.js';
import { ctaButton, setButtonDisabled } from '/src/ui/components/cta-button.js';
import { materialPicker } from '/src/ui/components/material-picker.js';

// Materiales por defecto si el módulo core aún no carga.
// Coinciden con claves reales del diccionario MATERIALS de core/acoustics.js.
const FALLBACK_MATERIALS = [
  'Pladur (cartón yeso)',
  'Pared enfoscada',
  'Parquet',
  'Cristal 6 mm',
  'Ventana doble',
  'Moqueta',
];

async function loadMaterialsList() {
  try {
    const mod = await import('/src/core/acoustics.js');
    if (mod && mod.MATERIALS && typeof mod.MATERIALS === 'object') {
      const keys = Object.keys(mod.MATERIALS);
      if (keys.length) return keys.sort((a, b) => a.localeCompare(b, 'es'));
    }
  } catch (_err) {
    // El módulo todavía no existe: usamos la lista de respaldo.
  }
  return FALLBACK_MATERIALS;
}

export function renderStep1(host, { next }) {
  host.innerHTML = `
    <header class="step__header">
      <h1 class="step__title" id="step-1-title">${t('step.1.title')}</h1>
      <p class="step__subtitle">${t('step.1.subtitle')}</p>
    </header>

    <section aria-labelledby="step-1-dims">
      <h2 id="step-1-dims" class="visually-hidden" style="position:absolute; left:-9999px;">Dimensiones</h2>
      <div class="room-fields">
        <label class="field">
          <span class="field__label">${t('step.1.largo')}</span>
          <input class="field__input" id="largo" type="text" inputmode="decimal"
                 autocomplete="off" placeholder="p. ej. 5,2"
                 aria-describedby="largo-hint" />
          <span class="field__hint" id="largo-hint">Mayor que 1 m</span>
        </label>

        <label class="field">
          <span class="field__label">${t('step.1.ancho')}</span>
          <input class="field__input" id="ancho" type="text" inputmode="decimal"
                 autocomplete="off" placeholder="p. ej. 4,0" />
          <span class="field__hint">Mayor que 1 m</span>
        </label>

        <label class="field">
          <span class="field__label">${t('step.1.altura')}</span>
          <input class="field__input" id="altura" type="text" inputmode="decimal"
                 autocomplete="off" placeholder="p. ej. 2,7" />
          <span class="field__hint">Entre 1,5 y 20 m</span>
        </label>
      </div>

      <div id="live-summary" class="kpi-row" aria-live="polite"></div>
    </section>

    <section aria-labelledby="step-1-mats">
      <h2 id="step-1-mats" class="step__title" style="font-size:1.15rem; margin-top: 1.5rem;">
        ${t('step.1.surfaces')}
      </h2>

      <div class="toggle-row" role="group" aria-label="Modo paredes">
        <button type="button" class="btn btn--secondary" id="walls-same">
          Aplicar el mismo material a todas las paredes
        </button>
        <button type="button" class="btn btn--ghost" id="walls-custom">
          Personalizar las 4
        </button>
      </div>

      <div class="surface-grid" id="surfaces"></div>
    </section>

    <div class="sticky-cta" id="sticky-cta"></div>
  `;

  const largoEl = host.querySelector('#largo');
  const anchoEl = host.querySelector('#ancho');
  const alturaEl = host.querySelector('#altura');
  const summary = host.querySelector('#live-summary');
  const surfacesHost = host.querySelector('#surfaces');
  const wallsSameBtn = host.querySelector('#walls-same');
  const wallsCustomBtn = host.querySelector('#walls-custom');

  // Hidratar desde state
  if (state.room.largo) largoEl.value = formatNum(state.room.largo);
  if (state.room.ancho) anchoEl.value = formatNum(state.room.ancho);
  if (state.room.altura) alturaEl.value = formatNum(state.room.altura);

  let wallsMode = 'same'; // 'same' | 'custom'
  if (
    state.surfaces.walls &&
    new Set(state.surfaces.walls).size > 1
  ) {
    wallsMode = 'custom';
  }

  const cta = ctaButton({
    label: 'Continuar',
    variant: 'primary',
    block: true,
    disabled: true,
    onClick: () => {
      if (validate(true)) next();
    },
  });
  host.querySelector('#sticky-cta').appendChild(cta);

  function commitDims() {
    const largo = parseNum(largoEl.value);
    const ancho = parseNum(anchoEl.value);
    const altura = parseNum(alturaEl.value);
    update({ room: { largo, ancho, altura } });
    refreshSummary();
    refreshCTA();
  }

  [largoEl, anchoEl, alturaEl].forEach((el) =>
    el.addEventListener('input', commitDims),
  );

  function refreshSummary() {
    const { largo, ancho, altura } = state.room;
    if (largo > 0 && ancho > 0 && altura > 0) {
      const volumen = largo * ancho * altura;
      const supSuelo = largo * ancho;
      const supParedes = 2 * (largo + ancho) * altura;
      const supTotal = 2 * supSuelo + supParedes;
      summary.innerHTML = `
        <div class="kpi"><div class="kpi__label">Volumen</div><div class="kpi__value">${formatNum(volumen)} m³</div></div>
        <div class="kpi"><div class="kpi__label">Superficie total</div><div class="kpi__value">${formatNum(supTotal)} m²</div></div>
        <div class="kpi"><div class="kpi__label">Suelo</div><div class="kpi__value">${formatNum(supSuelo)} m²</div></div>
      `;
    } else {
      summary.innerHTML = '<div class="kpi__label" style="padding:0.4rem 0;">Introduce las medidas para ver el resumen.</div>';
    }
  }

  function refreshCTA() {
    setButtonDisabled(cta, !validate(false));
  }

  function validate(showErrors) {
    const { largo, ancho, altura } = state.room;
    let ok = true;

    setFieldError(largoEl, '');
    setFieldError(anchoEl, '');
    setFieldError(alturaEl, '');

    if (!(largo > 1)) {
      ok = false;
      if (showErrors) setFieldError(largoEl, 'Largo debe ser mayor que 1 m');
    }
    if (!(ancho > 1)) {
      ok = false;
      if (showErrors) setFieldError(anchoEl, 'Ancho debe ser mayor que 1 m');
    }
    if (!(altura > 1.5) || altura > 20) {
      ok = false;
      if (showErrors) setFieldError(alturaEl, 'Altura entre 1,5 y 20 m');
    }
    return ok;
  }

  // Materiales
  loadMaterialsList().then((options) => {
    renderSurfaces(options);
  });

  function renderSurfaces(options) {
    surfacesHost.innerHTML = '';

    surfacesHost.appendChild(
      surfaceRow({
        label: t('step.1.ceiling'),
        value: state.surfaces.ceiling,
        options,
        onChange: (v) => update({ surfaces: { ceiling: v } }),
      }),
    );
    surfacesHost.appendChild(
      surfaceRow({
        label: t('step.1.floor'),
        value: state.surfaces.floor,
        options,
        onChange: (v) => update({ surfaces: { floor: v } }),
      }),
    );

    if (wallsMode === 'same') {
      const current = state.surfaces.walls?.[0] || options[0];
      surfacesHost.appendChild(
        surfaceRow({
          label: 'Paredes (las 4)',
          value: current,
          options,
          onChange: (v) => update({ surfaces: { walls: [v, v, v, v] } }),
        }),
      );
    } else {
      for (let i = 0; i < 4; i++) {
        const wallVal = state.surfaces.walls?.[i] || options[0];
        surfacesHost.appendChild(
          surfaceRow({
            label: t(`step.1.wall.${i + 1}`),
            value: wallVal,
            options,
            onChange: (v) => {
              const next = [...(state.surfaces.walls || [])];
              next[i] = v;
              update({ surfaces: { walls: next } });
            },
          }),
        );
      }
    }
  }

  wallsSameBtn.addEventListener('click', () => {
    wallsMode = 'same';
    const w = state.surfaces.walls?.[0] || FALLBACK_MATERIALS[0];
    update({ surfaces: { walls: [w, w, w, w] } });
    loadMaterialsList().then(renderSurfaces);
  });
  wallsCustomBtn.addEventListener('click', () => {
    wallsMode = 'custom';
    loadMaterialsList().then(renderSurfaces);
  });

  refreshSummary();
  refreshCTA();
}

function surfaceRow({ label, value, options, onChange }) {
  const row = document.createElement('div');
  row.className = 'surface-row';
  const lab = document.createElement('span');
  lab.className = 'surface-row__label';
  lab.textContent = label;
  row.appendChild(lab);
  row.appendChild(materialPicker({ value, options, onChange, label }));
  return row;
}

function parseNum(str) {
  if (typeof str !== 'string') return 0;
  const n = parseFloat(str.replace(',', '.').trim());
  return Number.isFinite(n) ? n : 0;
}
function formatNum(n) {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('es-ES', { maximumFractionDigits: 2 });
}
function setFieldError(input, msg) {
  const wrap = input.closest('.field');
  if (!wrap) return;
  let err = wrap.querySelector('.field__error');
  if (!msg) {
    wrap.classList.remove('field--invalid');
    if (err) err.remove();
    return;
  }
  wrap.classList.add('field--invalid');
  if (!err) {
    err = document.createElement('span');
    err.className = 'field__error';
    wrap.appendChild(err);
  }
  err.textContent = msg;
}
